import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { DEFAULT_TIME_PICKER_PROPS } from 'features/daily-plan/components/forms/shared/DateTimePickers/DateTimePickers.constants';
import { ENDPOINTS } from 'features/daily-plan/constants';
import { useShowAlert } from 'hooks';
import moment from 'moment';
import API from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import CustomDatePicker from 'UI/components/atoms/CustomDatePicker';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';

import { MAX_TASKS_LIMIT } from '../../DailyPlan.constants';

import { FIELD_KEYS, POPOVER_PROPS, RESCHEDULE_MODES } from './RescheduleActionsDialog.constants';
import { prepareDataToSend } from './RescheduleActionsDialog.utils';

const STRINGS = strings.dailyPlan.drawer.form;

export const RescheduleActionsDialog = ({
  mode = null,
  onClose = () => {},
  onRescheduleComplete = () => {},
  selectedItems
}) => {
  const { showAlert } = useShowAlert();
  const form = useForm();
  const { watch, setValue, register, unregister, errors, triggerValidation } = form;

  const formValues = watch();

  const [uiState, setUiState] = useState({
    isLoading: false,
    error: null
  });

  const totalSelected = selectedItems.data?.length;
  const isLimitExceeded = totalSelected > MAX_TASKS_LIMIT;

  const handleDateChange = (name, value) => {
    setValue(name, value ? moment(value) : null, { shouldValidate: true });
  };

  const onRescheduleSubmit = async () => {
    try {
      const isFormValid = mode === RESCHEDULE_MODES.Custom ? await triggerValidation() : true;

      if (!isFormValid) {
        setUiState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      setUiState(prev => ({ ...prev, isLoading: true, error: null }));

      const dataToSend = prepareDataToSend({
        selectedItems: selectedItems.data,
        formValues,
        rescheduleMode: mode
      });

      const response = await API.patch(ENDPOINTS.tasks, dataToSend);

      if (response.status === HTTPStatusCodes.Ok) {
        setUiState(prev => ({
          ...prev,
          isLoading: false,
          error: null
        }));
        showAlert({
          title: strings.dailyPlan.rescheduleActions.success,
          autoHideDuration: 5000
        });

        await onRescheduleComplete();
      }
    } catch (error) {
      setUiState(prev => ({
        ...prev,
        isLoading: false,
        error: strings.dailyPlan.rescheduleActions.fail
      }));
      showAlert({
        autoHideDuration: 5000,
        title: strings.dailyPlan.rescheduleActions.failTitle,
        error: strings.dailyPlan.rescheduleActions.fail
      });
    }
  };

  useEffect(() => {
    if (mode) {
      register(FIELD_KEYS.Date, { required: 'Date is required' });
      register(FIELD_KEYS.Time);
    }

    return () => {
      unregister(FIELD_KEYS.Date);
      unregister(FIELD_KEYS.Time);
    };
  }, [register, mode, unregister]);

  return (
    <DecisionDialog
      cancelButtonText="Cancel"
      confirmButtonText="Reschedule"
      fetching={uiState.isLoading}
      isOpened
      onClose={onClose}
      onConfirm={onRescheduleSubmit}
      severity="info"
      showCloseButton={false}
      showSeverityIcon={false}
      title={strings.dailyPlan.rescheduleActions.decisionDialog.title}
      withButtons="YesNo"
    >
      <When condition={isLimitExceeded}>
        <Alert severity="warning" className="margin-y1">
          {strings.formatString(strings.dailyPlan.rescheduleActions.limitExceeded, {
            totalSelected,
            maxItemsLimit: MAX_TASKS_LIMIT
          })}
        </Alert>
      </When>
      <When condition={mode === RESCHEDULE_MODES.Tomorrow}>
        <Typography variant="body1">
          {strings.dailyPlan.rescheduleActions.rescheduleOptions.rescheduleForTomorrow.description}
        </Typography>
      </When>
      <When condition={mode === RESCHEDULE_MODES.Custom}>
        <CustomDatePicker
          error={!!errors[FIELD_KEYS.Date]}
          helperText={errors[FIELD_KEYS.Date]?.message}
          isClearable
          label="Date"
          name={FIELD_KEYS.Date}
          onDateChange={handleDateChange}
          placeholder={STRINGS.placeholders.setDate}
          PopoverProps={POPOVER_PROPS}
          value={formValues[FIELD_KEYS.Date] || null}
        />
        <CustomDatePicker
          error={!!errors[FIELD_KEYS.Time]}
          helperText={errors[FIELD_KEYS.Time]?.message}
          isClearable
          label="Time"
          name={FIELD_KEYS.Time}
          onDateChange={handleDateChange}
          PopoverProps={POPOVER_PROPS}
          value={formValues[FIELD_KEYS.Time] || null}
          variant="keyboardTime"
          withTime
          {...DEFAULT_TIME_PICKER_PROPS}
        />
      </When>
      <When condition={uiState.error}>
        <Alert severity="error" className="margin-y1">
          {uiState.error}
        </Alert>
      </When>
    </DecisionDialog>
  );
};
